import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { Link } from "react-router-dom";
import api from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Wallet, Star } from "lucide-react";
import { toast } from "sonner";
import useRevalidate from "@/lib/useRevalidate";

export function Customers() {
  const { t } = useApp();
  const [list, setList] = useState([]);
  const [loyalty, setLoyalty] = useState({});  // { customer_id: {tier, spend_30d} }
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", language: "en", is_bulk: false });
  const load = () => api.get("/customers").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/customers/loyalty").then((r) => {
      const m = {};
      r.data.forEach((x) => { m[x.customer_id] = x; });
      setLoyalty(m);
    }).catch((e) => console.error("loyalty fetch failed:", e?.message));
  }, []);
  useRevalidate(load);
  const save = async () => {
    try { await api.post("/customers", form); toast.success("Saved"); setOpen(false); setForm({ name: "", phone: "", address: "", language: "en", is_bulk: false }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold">{t("customers")}</h1>
        <Button onClick={() => setOpen(true)} data-testid="add-customer-btn" className="h-12 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold"><Plus className="w-4 h-4 mr-2" />{t("add_customer")}</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {list.map((c) => {
          const l = loyalty[c.id];
          const isVip = l?.tier === "vip";
          const hasBalance = c.balance > 0;
          return (
            <Card key={c.id} className="p-4 rounded-2xl" data-testid={`customer-${c.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading font-semibold truncate">{c.name}</div>
                    {isVip && (
                      <span data-testid={`cust-tier-vip-${c.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-white bg-[#B45309] px-2 py-0.5 rounded">
                        <Star className="w-2.5 h-2.5" /> {t("tier_vip")}
                      </span>
                    )}
                    {l && !isVip && (
                      <span data-testid={`cust-tier-regular-${c.id}`} className="text-[10px] uppercase font-bold text-stone-600 bg-stone-100 px-2 py-0.5 rounded">
                        {t("tier_regular")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">{c.phone} • {c.address}</div>
                  {l && <div className="text-[10px] text-stone-500 mt-0.5 font-mono">30d: ₹{l.spend_30d.toFixed(0)}</div>}
                  {c.is_bulk && <span className="mt-1 inline-block text-[10px] uppercase font-bold text-[#166534] bg-green-100 px-2 py-0.5 rounded">{t("bulk_customer")}</span>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs uppercase text-stone-500 tracking-widest">{t("balance")}</div>
                  <div className={`font-mono font-semibold ${hasBalance ? "text-red-600" : "text-[#166534]"}`}>₹{c.balance.toFixed(2)}</div>
                </div>
              </div>
              {hasBalance && (
                <Link to={`/bills?customer_id=${c.id}&preselect=1`} data-testid={`cust-collect-${c.id}`}
                      className="mt-3 h-10 rounded-lg bg-[#166534] hover:bg-[#14532D] text-white text-xs font-semibold flex items-center justify-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" /> {t("collect_pending")}
                </Link>
              )}
            </Card>
          );
        })}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>{t("add_customer")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" className="h-12 rounded-lg" />
            <Input placeholder={t("phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" className="h-12 rounded-lg" />
            <Input placeholder={t("address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cust-addr" className="h-12 rounded-lg" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_bulk} onChange={(e) => setForm({ ...form, is_bulk: e.target.checked })} data-testid="cust-bulk" /> {t("bulk_customer")}</label>
          </div>
          <div className="flex justify-end gap-2 mt-3"><Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button onClick={save} data-testid="cust-save" className="bg-[#B45309] hover:bg-[#92400E] text-white">{t("save")}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Suppliers() {
  const { t } = useApp();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const load = () => api.get("/suppliers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  useRevalidate(load);
  const save = async () => { try { await api.post("/suppliers", form); toast.success("Saved"); setOpen(false); setForm({ name: "", phone: "", address: "" }); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } };
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold">{t("suppliers")}</h1>
        <Button onClick={() => setOpen(true)} data-testid="add-supplier-btn" className="h-12 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold"><Plus className="w-4 h-4 mr-2" />{t("add_supplier")}</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {list.map((c) => (
          <Card key={c.id} className="p-4 rounded-2xl" data-testid={`supplier-${c.id}`}>
            <div className="flex justify-between items-start">
              <div><div className="font-heading font-semibold">{c.name}</div><div className="text-xs text-stone-500">{c.phone} • {c.address}</div></div>
              <div className="text-right"><div className="text-xs uppercase text-stone-500 tracking-widest">{t("balance")}</div><div className={`font-mono font-semibold ${c.balance > 0 ? "text-red-600" : "text-[#166534]"}`}>₹{c.balance.toFixed(2)}</div></div>
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>{t("add_supplier")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sup-name" className="h-12 rounded-lg" />
            <Input placeholder={t("phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sup-phone" className="h-12 rounded-lg" />
            <Input placeholder={t("address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="sup-addr" className="h-12 rounded-lg" />
          </div>
          <div className="flex justify-end gap-2 mt-3"><Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button onClick={save} data-testid="sup-save" className="bg-[#B45309] hover:bg-[#92400E] text-white">{t("save")}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
